import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import locales from '../../content.json';
import s from './index.module.scss';

import CalculateOfferPrices from '../../utils/scripts/calculateOfferPrices';
import { calculatePrice } from '../../utils/scripts/calculatePrice';

import Option from './Option';
import PromoCode from '../../components/common/promoCode';

import { getPricesConfig } from '../getPricesConfig';
import { DEFAULT_DISCOUNT } from '../discountConfig';
import { RootState } from '../../store';
import { PAYMENT_TARIFF } from '../../types';
import { getParameterFromURL } from '../../utils/scripts';
import { getCurrencySymbol } from '../../utils/scripts/currency';
import {
  OfferProps,
  adjustDayPrice,
  formatOptionTitle,
  priceFirstPart,
  priceSecondPart,
  useDayPrices,
  useOfferProducts,
  useSelectProduct,
} from './offerShared';

/** Discount tariffs that get the special-offer styling and the -77% label. */
const SPECIAL_OFFER_TARIFFS = [
  PAYMENT_TARIFF.V22,
  PAYMENT_TARIFF.V28,
  PAYMENT_TARIFF.V30,
  PAYMENT_TARIFF.V32,
  PAYMENT_TARIFF.V36,
  PAYMENT_TARIFF.V38,
  PAYMENT_TARIFF.V40,
  PAYMENT_TARIFF.V42,
];

const Offer = ({ currentProduct, secondScreenWrapper }: OfferProps) => {
  const tariff = useSelector((state: RootState) => state.data.tariff);

  const { firstPriceConfig, secondPriceConfig, thirdPriceConfig } =
    getPricesConfig(tariff).everydoggyNewDesign;
  const { extraOptions } = getPricesConfig(tariff);

  const currency = getCurrencySymbol();
  const prices = useOfferProducts(firstPriceConfig, secondPriceConfig, thirdPriceConfig, tariff);
  const doOnClick = useSelectProduct();

  const showWeeklyText = tariff === PAYMENT_TARIFF.V47;

  const p20v1RedLabelDiscountNoSpecial =
    getParameterFromURL('p20v1RedLabelDiscountNoSpecial') === 'true' && tariff === PAYMENT_TARIFF.V41;

  const specialOffer = SPECIAL_OFFER_TARIFFS.includes(tariff);
  const isMostPopularFirstOption = tariff === PAYMENT_TARIFF.V47;
  const labelDiscountText = specialOffer ? '77' : `${DEFAULT_DISCOUNT}`;

  const firstPrice = useMemo(() => {
    const { firstOption } = prices;
    const { defaultPricesPerWeek } = firstPriceConfig;

    if (firstOption?.duration?.trial_period_unit === 'week' || firstOption?.duration?.trial_period_value === 7) {
      return CalculateOfferPrices.setPriceFullV2({
        product: firstOption,
        defaultPrice: defaultPricesPerWeek,
        calculateFromIntro: true,
      });
    }
    return CalculateOfferPrices.setPriceInWeeksIntro({
      product: firstOption,
      defaultPricesPerWeek,
      calculateFromIntro: true,
    });
  }, [prices, firstPriceConfig]);

  const secondPrice = useMemo(() => {
    const { secondOption } = prices;
    const { defaultPricesPerWeek } = secondPriceConfig;

    return calculatePrice(secondOption, {
      target: 'perWeek',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: defaultPricesPerWeek,
    });
  }, [prices, secondPriceConfig]);

  const thirdPrice = useMemo(() => {
    const { thirdOption } = prices;
    const { defaultPricesPerWeek } = thirdPriceConfig;

    return calculatePrice(thirdOption, {
      target: 'perWeek',
      source: 'auto',
      calculateFromIntro: true,
      defaultPrice: defaultPricesPerWeek,
    });
  }, [prices, thirdPriceConfig]);

  const { firstPriceInDay, secondPriceInDay, thirdPriceInDay } = useDayPrices(
    prices,
    firstPriceConfig,
    secondPriceConfig,
    thirdPriceConfig
  );

  const p13MixPaywallDiscountPercent = getParameterFromURL('p13MixPaywallDiscountPercent');

  let firstOldPriceInWeek: string | number;
  let secondOldPriceInWeek: string | number;
  let thirdOldPriceInWeek: string | number;

  if (p13MixPaywallDiscountPercent && (tariff === 'v37' || tariff === 'v38')) {
    firstOldPriceInWeek = '5.38';
    secondOldPriceInWeek = '4.41';
    thirdOldPriceInWeek = '3.14';
  } else if (p13MixPaywallDiscountPercent && (tariff === 'v31' || tariff === 'v32')) {
    firstOldPriceInWeek = '13.49';
    secondOldPriceInWeek = '7.54';
    thirdOldPriceInWeek = '5.46';
  } else {
    firstOldPriceInWeek = firstPriceConfig?.defaultOldPricesForPeriod || '10.62';
    secondOldPriceInWeek = secondPriceConfig?.defaultOldPricesForPeriod || '2.79';
    thirdOldPriceInWeek = thirdPriceConfig?.defaultOldPricesForPeriod || '2.02';
  }

  if (p20v1RedLabelDiscountNoSpecial) {
    firstOldPriceInWeek = 4.97;
    secondOldPriceInWeek = 4.07;
    thirdOldPriceInWeek = 2.89;
  }

  return (
    <div className={`${s.optionsWrapper} ${s.optionStyleV2} ${specialOffer ? s.tariffV5 : ''}`}>
      <h2 className={s.optionsTitle}>{locales[window.userLang].tariff_title_choose}</h2>
      <PromoCode tariff={tariff} secondScreenWrapper={secondScreenWrapper} />
      <div className={s.options}>
        <Option
          className={`${s.option} ${p20v1RedLabelDiscountNoSpecial ? s.redLabel : ''} ${
            isMostPopularFirstOption ? s.mostPopularFirstOption : ''
          }`}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.firstOption?.id}
          productName={firstPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, firstPriceConfig.defaultOldPrices)}
        >
          {isMostPopularFirstOption && (
            <div className={s.bestOffer}>{extraOptions?.mostPopularTitle}</div>
          )}

          {p20v1RedLabelDiscountNoSpecial && (
            <p className={s.labelDiscount}>-{labelDiscountText}%</p>
          )}
          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(firstPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {firstOldPriceInWeek}
              </span>{' '}
              {currency}
              {`${firstPrice}`} {showWeeklyText ? locales[window.userLang].payment_text_56 : null}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(firstPriceInDay)}.{priceSecondPart(firstPriceInDay)}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>

        <Option
          className={`${s.option} ${p20v1RedLabelDiscountNoSpecial ? s.redLabel : ''} ${
            !isMostPopularFirstOption ? s.mostPopularFirstOption : ''
          }`}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.secondOption?.id}
          productName={secondPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, secondPriceConfig.defaultOldPrices)}
        >
          {!isMostPopularFirstOption && (
            <div className={s.bestOffer}>{extraOptions?.mostPopularTitle}</div>
          )}

          {p20v1RedLabelDiscountNoSpecial && (
            <p className={s.labelDiscount}>-{labelDiscountText}%</p>
          )}
          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(secondPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {secondOldPriceInWeek}
              </span>{' '}
              {currency}
              {secondPrice} {locales[window.userLang].payment_text_56}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(secondPriceInDay)}.{priceSecondPart(secondPriceInDay)}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>

        <Option
          className={`${s.option} ${p20v1RedLabelDiscountNoSpecial ? s.redLabel : ''}`}
          selectedClassName={s.option__selected}
          currentProduct={currentProduct}
          optionValue={prices.thirdOption?.id}
          productName={thirdPriceConfig.productName}
          doOnClick={(val, pr) => doOnClick(val, pr, thirdPriceConfig.defaultOldPrices)}
        >
          {p20v1RedLabelDiscountNoSpecial && (
            <p className={s.labelDiscount}>-{labelDiscountText}%</p>
          )}
          <div className={s.radio}></div>
          <div className={s.priceWrapepr}>
            <p className={s.priceWrapeprTitle}>{formatOptionTitle(thirdPriceConfig.titleWeek)}</p>
            <p className={s.price}>
              <span className={s.lineRed}>
                {currency}
                {thirdOldPriceInWeek}
              </span>{' '}
              {currency}
              {thirdPrice} {locales[window.userLang].payment_text_56}
            </p>
          </div>

          <div className={s.priceDay}>
            <div className={s.priceDayWrapper}>
              <p className={s.dollar}>{currency}</p>
              <div className={s.firstPart}>
                {priceFirstPart(adjustDayPrice(thirdPriceInDay))}.{priceSecondPart(adjustDayPrice(thirdPriceInDay))}
              </div>
            </div>
            <div className={s.priceDayText}>{firstPriceConfig.perDay}</div>
          </div>
        </Option>
      </div>
    </div>
  );
};

export default Offer;
